import {Trans} from '@lingui/macro';
import {isFunction} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Handler, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonChevron} from '../../../../libs/shared/ui/src/components/buttons/buttonChevron';
import {
  ButtonChrome,
  ButtonInheritableProps,
} from '../../../../libs/shared/ui/src/components/buttons/buttonChrome';
import {
  ButtonVariantsEnum,
  findButtonColors,
} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardEventHandlers} from '../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipShortcutFormatter} from '../../../../libs/shared/ui/src/components/tooltips/tooltipShortcutFormatter';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionDenied} from '../../../../srcCommon/permissions/permissionDenied';
import {
  useComposeDiscussionOfflinePermission,
  useComposeMessageOfflinePermission,
} from '../../../../srcCommon/permissions/useOfflinePermission';
import {handlerWithTextInputIntent} from '../../../helpers/keyboardHandlerHelpers';
import {PermissionResult} from '../../../helpers/permissionHelpers';
import {DropdownItemIcon} from '../../common/dropdowns/dropdownItemIcon';
import {PermissionDropdownItem} from '../../common/dropdowns/permissionDropdownItem';

/*
 * Props.
 */

export interface ComposeButtonProps {
  canCompose: boolean;
  canComposeInternal: boolean;
  onComposeClick: Handler;
  onComposeInternalClick: Handler;
  size: number;
  hasUserTeamTrialExpired: boolean;
  hasTrialExpired: boolean;
}

/*
 * Style.
 */

const StyledButtonChrome = styled(ButtonChrome)`
  width: 100%;
  max-width: 168px;
`;

const StyledComposeButton = styled(ChromelessButton)`
  flex-grow: 1;
`;

const StyledComposeButtonContent = styled(ButtonContent)`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  grid-column-gap: 6px;
  justify-content: center;

  padding: 6px 18px 7px;
`;

const StyledComposeButtonIcon = styled(Icon)`
  margin-top: -1px;
  margin-bottom: -2px;
`;

const StyledComposeButtonLabel = styled.div`
  ${center()}
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  flex: 1;
`;

const StyledButtonChevronWrapper = styled.div`
  display: flex;
  align-items: stretch;
`;

/*
 * Component.
 */

export const ComposeButton: FC<ComposeButtonProps> = (props) => {
  const {canCompose, canComposeInternal, onComposeClick, onComposeInternalClick} = props;
  const keyHandlers: KeyboardEventHandlers = {};

  const messageOfflinePermission = useComposeMessageOfflinePermission();
  const discussionOfflinePermission = useComposeDiscussionOfflinePermission();

  if (canCompose && messageOfflinePermission.isAllowed) {
    keyHandlers[KeyboardActionsEnum.COMPOSE] = handlerWithTextInputIntent(onComposeClick);
  }

  if (canComposeInternal && discussionOfflinePermission.isAllowed) {
    keyHandlers[KeyboardActionsEnum.COMPOSE_DISCUSSION] = handlerWithTextInputIntent(onComposeInternalClick);
  }

  return (
    <KeyboardShortcuts handlers={keyHandlers}>
      {renderButton(props, messageOfflinePermission, discussionOfflinePermission)}
    </KeyboardShortcuts>
  );
};

const composeButtonVariant = ButtonVariantsEnum.PRIMARY;

function renderButton(
  props: ComposeButtonProps,
  messageOfflinePermission: PermissionResult,
  discussionOfflinePermission: PermissionResult,
) {
  const {canCompose, hasUserTeamTrialExpired, hasTrialExpired} = props;
  const isDisabled =
    !canCompose || !messageOfflinePermission.isAllowed || hasTrialExpired || hasUserTeamTrialExpired;

  const renderComposeDropdown: RendererOf<PopoverTransferProps> = (modalProps) =>
    renderDropdown(modalProps, props, messageOfflinePermission, discussionOfflinePermission);

  return (
    <StyledButtonChrome isDisabled={isDisabled} variant={composeButtonVariant} size={VisualSizesEnum.MEDIUM}>
      {(buttonProps) => (
        <>
          <StyledTooltipCoordinator render={() => renderComposeTooltip(props, messageOfflinePermission)}>
            {renderDefaultButton(buttonProps, props)}
          </StyledTooltipCoordinator>
          <StyledButtonChevronWrapper>
            {renderOptionsButton(buttonProps, renderComposeDropdown, discussionOfflinePermission, props)}
          </StyledButtonChevronWrapper>
        </>
      )}
    </StyledButtonChrome>
  );
}

function renderDefaultButton(buttonProps: ButtonInheritableProps, props: ComposeButtonProps) {
  return (
    <UserThemeConsumer>
      {(theme) => {
        const buttonColors = findButtonColors(theme);
        const composeButtonColors = buttonColors[composeButtonVariant];
        const actualColor = isFunction(composeButtonColors.text)
          ? composeButtonColors.text()
          : composeButtonColors.text;
        return (
          <StyledComposeButton {...buttonProps} onClick={props.onComposeClick} interactionId="compose-button">
            <StyledComposeButtonContent {...buttonProps}>
              {props.size >= 200 && (
                <StyledComposeButtonIcon
                  name="compose"
                  color={buttonProps.isDisabled ? composeButtonColors.textDisabled : actualColor}
                />
              )}
              <StyledComposeButtonLabel>
                <Trans id="workspace-topbar-compose">Compose</Trans>
              </StyledComposeButtonLabel>
            </StyledComposeButtonContent>
          </StyledComposeButton>
        );
      }}
    </UserThemeConsumer>
  );
}

function renderOptionsButton(
  buttonProps: ButtonInheritableProps,
  renderComposeDropdown: RendererOf<PopoverTransferProps>,
  discussionOfflinePermission: PermissionResult,
  props: ComposeButtonProps,
) {
  const isDisabled =
    !props.canComposeInternal ||
    !discussionOfflinePermission.isAllowed ||
    props.hasTrialExpired ||
    props.hasUserTeamTrialExpired;

  return <ButtonChevron {...buttonProps} renderDropdown={renderComposeDropdown} isDisabled={isDisabled} />;
}

function renderComposeTooltip(props: ComposeButtonProps, permission: PermissionResult) {
  if (!props.canCompose) {
    return <Trans id="workspace-topbar-compose-tooltip-disabled">A channel is required to compose</Trans>;
  }

  if (!permission.isAllowed) {
    return <PermissionDenied reason={permission.reason} />;
  }

  return (
    <Trans id="workspace-topbar-compose-tooltip">
      New message <TooltipShortcutFormatter action={KeyboardActionsEnum.COMPOSE} />
    </Trans>
  );
}

function renderDropdown(
  modalProps: PopoverTransferProps,
  props: ComposeButtonProps,
  messageOfflinePermission: PermissionResult,
  discussionOfflinePermission: PermissionResult,
) {
  const {anchor, onRequestClose} = modalProps;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <SimpleDropdown anchor={anchor} onRequestClose={onRequestClose}>
          <PermissionDropdownItem
            onSelect={props.onComposeClick}
            isDisabled={!props.canCompose}
            offlinePermission={messageOfflinePermission}
          >
            <DropdownItemIcon label={null} iconName="compose" color={theme.greys.shade60} />
            <DropdownItemContent>
              <Trans id="workspace-topbar-composedropdown-message">New message</Trans>
            </DropdownItemContent>
          </PermissionDropdownItem>
          <PermissionDropdownItem
            onSelect={props.onComposeInternalClick}
            offlinePermission={discussionOfflinePermission}
          >
            <DropdownItemIcon label={null} iconName="chat" color={theme.greys.shade60} />
            <DropdownItemContent>
              <Trans id="workspace-topbar-composedropdown-discussion">New discussion</Trans>
            </DropdownItemContent>
          </PermissionDropdownItem>
        </SimpleDropdown>
      )}
    </UserThemeConsumer>
  );
}
